'use client';

import * as React from 'react';
import { Switch as SwitchPrimitive } from 'radix-ui';
import { useId } from 'react';

import { cn } from '@/lib/utils';

function SwitchRoot({ className, ...props }: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        'peer data-[state=checked]:bg-primary data-[state=unchecked]:bg-input focus-visible:ring-ring/50 inline-flex h-6 w-10 shrink-0 items-center rounded-full border-2 border-transparent transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          'bg-background pointer-events-none block size-5 rounded-full shadow-xs ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0 data-[state=checked]:rtl:-translate-x-4',
        )}
      />
    </SwitchPrimitive.Root>
  );
}

import { Label } from '@/components/ui/label';

type SwitchProps = {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
} & React.ComponentProps<typeof SwitchPrimitive.Root>;

function Switch({ checked, onChange, ...props }: SwitchProps) {
  const id = useId();
  return (
    <div className="inline-flex items-center gap-2">
      <SwitchRoot
        id={id}
        className="[&_span]:rounded rounded-md"
        checked={checked}
        onCheckedChange={onChange}
        {...props}
      />
      <Label htmlFor={id} className="sr-only">
        Switch
      </Label>
    </div>
  );
}

export { Switch };
